import React, { useState, useEffect, useCallback } from "react";
import { RotateCcw, Trash2, Calendar, User, Eye, AlertCircle } from "lucide-react";
import { trashService, DeletedBot } from "../../services/trashService";
import { formatDateTime } from "../../utils/dateFormatter";
import TrashDetailModal from "../TrashDetailModal";
import "../../styles/components/deleted-bots.css";

const DeletedBots: React.FC = () => {
  const [bots, setBots] = useState<DeletedBot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBots, setSelectedBots] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [selectedBot, setSelectedBot] = useState<DeletedBot | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const loadBots = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await trashService.getDeletedBots(page, limit);
      setBots(response.data);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Có lỗi xảy ra khi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    loadBots();
  }, [loadBots]);

  const handleRestore = async (botId: number) => {
    try {
      await trashService.restoreBot(botId);
      setBots(bots.filter(bot => bot.id !== botId));
      setSelectedBots(prev => prev.filter(id => id !== botId));
      setTotal(prev => prev - 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể khôi phục bot');
    }
  };

  const handlePermanentDelete = async (botId: number) => {
    try {
      await trashService.permanentDeleteBot(botId);
      setBots(bots.filter(bot => bot.id !== botId));
      setSelectedBots(prev => prev.filter(id => id !== botId));
      setTotal(prev => prev - 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể xóa vĩnh viễn bot');
    }
  };

  const handleBulkRestore = async () => {
    try {
      await trashService.bulkRestoreBots(selectedBots);
      setBots(bots.filter(bot => !selectedBots.includes(bot.id)));
      setSelectedBots([]);
      setTotal(prev => prev - selectedBots.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể khôi phục các bot');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await trashService.bulkDeleteBots(selectedBots);
      setBots(bots.filter(bot => !selectedBots.includes(bot.id)));
      setSelectedBots([]);
      setTotal(prev => prev - selectedBots.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể xóa vĩnh viễn các bot');
    }
  };

  const toggleSelectBot = (botId: number) => {
    setSelectedBots(prev => 
      prev.includes(botId) 
        ? prev.filter(id => id !== botId)
        : [...prev, botId]
    );
  };

  const selectAll = () => {
    setSelectedBots(bots.map(bot => bot.id));
  };

  const deselectAll = () => {
    setSelectedBots([]);
  };

  const handleViewDetails = (bot: DeletedBot) => {
    setSelectedBot(bot);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedBot(null);
  };

  if (loading) {
    return (
      <div className="trash-loading">
        <div className="trash-loading__spinner"></div>
        <p>Đang tải danh sách bot đã xóa...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trash-empty">
        <AlertCircle size={48} className="trash-empty__icon" />
        <h3 className="trash-empty__title">Lỗi tải dữ liệu</h3>
        <p className="trash-empty__description">{error}</p>
        <button onClick={loadBots} className="trash-empty__retry">
          Thử lại
        </button>
      </div>
    );
  }

  return (
    <div className="deleted-bots">
      <div className="deleted-bots__header">
        <div className="deleted-bots__info">
          <h3 className="deleted-bots__title">Bot kiểm duyệt đã xóa ({bots.length})</h3>
          <p className="deleted-bots__description">Danh sách các bot kiểm duyệt đã bị xóa và có thể khôi phục</p>
        </div>
        
        {bots.length > 0 && (
          <div className="deleted-bots__bulk-actions">
            <button 
              className="deleted-bots__bulk-btn deleted-bots__bulk-btn--restore"
              onClick={handleBulkRestore}
              disabled={selectedBots.length === 0}
            >
              <RotateCcw size={16} />
              Khôi phục ({selectedBots.length})
            </button>
            <button 
              className="deleted-bots__bulk-btn deleted-bots__bulk-btn--delete"
              onClick={handleBulkDelete}
              disabled={selectedBots.length === 0}
            >
              <Trash2 size={16} />
              Xóa vĩnh viễn ({selectedBots.length})
            </button>
            <button 
              className="deleted-bots__bulk-btn deleted-bots__bulk-btn--select"
              onClick={selectedBots.length === bots.length ? deselectAll : selectAll}
            >
              {selectedBots.length === bots.length ? "Bỏ chọn tất cả" : "Chọn tất cả"}
            </button>
          </div>
        )}
      </div>

      {bots.length === 0 ? (
        <div className="trash-empty">
          <Trash2 size={48} className="trash-empty__icon" />
          <h3 className="trash-empty__title">Không có bot nào trong thùng rác</h3>
          <p className="trash-empty__description">Tất cả bot kiểm duyệt đều đang hoạt động bình thường</p>
        </div>
      ) : (
        <div className="deleted-bots__list">
          {bots.map((bot) => (
            <div key={bot.id} className="deleted-bots__item">
              <div className="deleted-bots__checkbox">
                <input
                  type="checkbox"
                  checked={selectedBots.includes(bot.id)}
                  onChange={() => toggleSelectBot(bot.id)}
                />
              </div>
              
              <div className="deleted-bots__content">
                <div className="deleted-bots__item-header">
                  <h4 className="deleted-bots__item-name">{bot.name}</h4>
                  <span className="deleted-bots__item-status deleted-bots__item-status--deleted">Đã xóa</span>
                </div>
                
                <div className="deleted-bots__meta">
                  <div className="deleted-bots__meta-item">
                    <User size={14} />
                    <span>Xóa bởi: {bot.deletedBy}</span>
                  </div>
                  <div className="deleted-bots__meta-item">
                    <Calendar size={14} />
                    <span>Xóa lúc: {formatDateTime(bot.deletedAt)}</span>
                  </div>
                  <div className="deleted-bots__meta-item">
                    <span>Trạng thái: {bot.isActive ? "Hoạt động" : "Tạm dừng"}</span>
                  </div>
                </div>
                
                <div className="deleted-bots__description">
                  {bot.description}
                </div>
                
                <div className="deleted-bots__keywords">
                  <span className="deleted-bots__keywords-label">Từ khóa:</span>
                  <div className="deleted-bots__keywords-list">
                    {bot.keywords.map((keyword, index) => (
                      <span key={index} className="deleted-bots__keyword-tag">
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="deleted-bots__actions">
                <button 
                  className="deleted-bots__action-btn deleted-bots__action-btn--preview"
                  onClick={() => handleViewDetails(bot)}
                  title="Xem chi tiết"
                >
                  <Eye size={16} />
                </button>
                <button 
                  className="deleted-bots__action-btn deleted-bots__action-btn--restore"
                  onClick={() => handleRestore(bot.id)}
                  title="Khôi phục"
                >
                  <RotateCcw size={16} />
                </button>
                <button 
                  className="deleted-bots__action-btn deleted-bots__action-btn--delete"
                  onClick={() => handlePermanentDelete(bot.id)}
                  title="Xóa vĩnh viễn"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Pagination */}
      {total > limit && (
        <div className="deleted-bots__pagination">
          <button 
            onClick={() => setPage(prev => Math.max(1, prev - 1))}
            disabled={page === 1}
            className="deleted-bots__pagination-btn"
          >
            Trước
          </button>
          <span className="deleted-bots__pagination-info">
            Trang {page} / {Math.ceil(total / limit)}
          </span>
          <button 
            onClick={() => setPage(prev => prev + 1)}
            disabled={page >= Math.ceil(total / limit)}
            className="deleted-bots__pagination-btn"
          >
            Sau
          </button>
        </div>
      )}
      
      {/* Detail Modal */}
      <TrashDetailModal
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        item={selectedBot}
        type="bot"
      />
    </div>
  );
};

export default DeletedBots;
